import { Link, useForm } from "@inertiajs/react"

import type { SharedProps } from "@/types"
import { AuthCard } from "@/components/auth-card"
import { AuthShell } from "@/components/auth-shell"
import { AuthStatus } from "@/components/auth-status"
import { Button } from "@/components/ui/button"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"

type PasswordResetRequestPageProps = SharedProps & {
  paths: {
    requestReset: string
    signIn: string
  }
  values: {
    email?: string
  }
}

export default function PasswordResetRequestPage({
  flash,
  paths,
  values,
}: PasswordResetRequestPageProps) {
  const form = useForm({
    email: values.email ?? "",
  })

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    form.post(paths.requestReset)
  }

  return (
    <AuthShell
      heroDescription="Forgotten passwords happen. Enter your email and we'll send a secure reset link."
      heroPoints={[
        "No account lockout — just one quick step.",
        "Check your spam folder if the email takes a moment.",
        "Only verified accounts can request a reset link.",
      ]}
      heroTitle="We'll get you back in."
      pageTitle="Reset your password"
    >
      <div className="flex flex-col gap-4">
        <AuthStatus flash={flash} />
        <AuthCard
          description="Enter your verified email and we’ll send reset instructions."
          eyebrow="Recovery"
          title="Forgot your password?"
        >
          <form onSubmit={submit}>
            <FieldGroup>
              <Field data-invalid={!!form.errors.email}>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input
                  aria-invalid={!!form.errors.email}
                  autoComplete="email"
                  id="email"
                  name="email"
                  onChange={(event) => form.setData("email", event.target.value)}
                  placeholder="name@example.com"
                  required
                  type="email"
                  value={form.data.email}
                />
                <FieldError>{form.errors.email}</FieldError>
              </Field>
              <Field>
                <Button className="w-full" disabled={form.processing} type="submit">
                  {form.processing ? "Sending..." : "Send reset link"}
                </Button>
                <FieldDescription className="text-center">
                  Remembered it? <Link href={paths.signIn}>Sign in</Link>
                </FieldDescription>
              </Field>
            </FieldGroup>
          </form>
        </AuthCard>
      </div>
    </AuthShell>
  )
}
